import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Alert, Button } from "@repo/ui";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RootState } from "../../../../redux/store";
import AnimatedCard from "../../../../components/AnimatedCard/AnimatedCard";
import PageContainer from "../../../../components/PageContainer/PageContainer";
import StepIndicator from "../../../../components/StepIndicator/StepIndicator";
import withAuth from "../../../../hoc/withAuth/withAuth";
import { gsap } from "gsap";
import { getWithdrawSteps } from "../../constants/steps";
import BackButton from "../../../../components/BackButton";
import { sumGiftsByGiftTableID } from "../../../../services/giftApiService";
import { withTranslations } from "../../../../hoc/withTranslations/withTranslations";
import {
  getSuccessfulWithdrawalsCountByGiftTable,
  getTotalSuccessfulWithdrawalsByGiftTable,
} from "../../../../services/withdrawApiService";

const LOCALE = "modules_MyGiftTable_pages_WithdrawPage_WithdrawPageStep1";
const STEP1_CACHE_KEY = "withdrawStep1Cache";

const WithdrawPageStep1: React.FC = () => {
  const { t } = useTranslation(LOCALE);
  const steps = getWithdrawSteps(t);

  const navigate = useNavigate();
  const [alert, setAlert] = useState<{
    message: React.ReactNode;
    level: "info" | "warn" | "error" | "success";
  } | null>(null);
  const alertRef = useRef<HTMLDivElement>(null);

  const [withdrawalCount, setWithdrawalCount] = useState<number | null>(null);
  const [searchParams] = useSearchParams();
  const stripeFlowEnabled =
    (searchParams.get("withdrawFlow") || "").toLowerCase() === "stripe";

  const plan = useSelector((state: RootState) => state.giftTable.plan);
  const retentionFeeRate =
    useSelector((state: RootState) => state.giftTable.retentionFeeRate) || 0;
  const tableID = useSelector((state: RootState) => state.giftTable.ID);

  // New local state: collected sum from API
  const [collectedSum, setCollectedSum] = useState<number>(0);
  const [alreadyWithdrawn, setAlreadyWithdrawn] = useState<number>(0); // New state

  // Update calculations without physical products
  const computedRetentionValue = collectedSum * retentionFeeRate;

  // Fetch collected sum from the API using tableID.
  // Updated fetch data function
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (tableID) {
          const [
            collectedResult,
            withdrawnResult,
            countResult, // Add count result
          ] = await Promise.all([
            sumGiftsByGiftTableID(tableID),
            getTotalSuccessfulWithdrawalsByGiftTable(tableID),
            getSuccessfulWithdrawalsCountByGiftTable(tableID), // New call
          ]);

          setCollectedSum(collectedResult.sum);
          setAlreadyWithdrawn(withdrawnResult.total);
          setWithdrawalCount(countResult.count); // Set withdrawal count

          // Cache for reuse in step2
          localStorage.setItem(
            STEP1_CACHE_KEY,
            JSON.stringify({
              tableID,
              collectedSum: collectedResult.sum,
              alreadyWithdrawn: withdrawnResult.total,
              withdrawalCount: countResult.count,
              timestamp: Date.now(),
            })
          );
        }
      } catch (error) {
        console.error("Error fetching data", error);
        setCollectedSum(0);
        setAlreadyWithdrawn(0);
        setWithdrawalCount(0);

        localStorage.removeItem(STEP1_CACHE_KEY);
      }
    };

    fetchData();
  }, [tableID]);

  // Show an alert if it's the first withdrawal.
  useEffect(() => {
    if (withdrawalCount === 0) {
      setAlert({
        message: t("firstWithdrawalAlert"),
        level: "info",
      });
    } else {
      setAlert(null);
    }
  }, [withdrawalCount, t]);

  // Animate alert appearance.
  useEffect(() => {
    if (alertRef.current) {
      gsap.fromTo(
        alertRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 0.5 }
      );
    }
  }, [alert]);

  const handleContinue = () => {
    const nextUrl = stripeFlowEnabled
      ? "/my-table/withdraw/step2?withdrawFlow=stripe"
      : "/my-table/withdraw/step2";
    navigate(nextUrl);
  };

  const handleBack = () => {
    navigate(`/my-table`);
  };

  return (
    <PageContainer>
      <div className="mt-16"></div>
      <StepIndicator steps={steps} currentStep={1} progressPercentage={25} />
      <AnimatedCard className="bg-white max-w-lg w-full">
        <BackButton text="Regresar" onClick={handleBack} />

        <h2 className="text-2xl font-bold mb-4 text-primary">{t("title")}</h2>
        {alert && (
          <div ref={alertRef}>
            <Alert message={alert.message} level={alert.level} />
          </div>
        )}
        <div className="text-center">
          <p className="text-gray-600 mb-8">{t("instructions")}</p>

          {/* Total collected value block */}
          <div className="bg-background p-4">
            <div className="text-gray-700 text-sm">{t("totalValueLabel")}</div>
            <div className="text-gray-900 text-2xl font-bold">
              {collectedSum?.toLocaleString("es-MX", {
                style: "currency",
                currency: "MXN",
              }) || "$0.00"}
            </div>
          </div>

          {/* Plan and Retention block */}
          {plan === "percentage" && (
            <div className="bg-background p-4 mb-4 mt-4">
              <div className="text-gray-700 text-sm">
                {t("planDescription")}
              </div>
              <div className="text-gray-900 text-xl font-bold">
                {t("commissionPlanLabel")} {(retentionFeeRate * 100).toFixed(2)}
                %
              </div>
              <div className="text-gray-700 mt-2">
                {t("retentionDescription")}
              </div>
              <div className="text-gray-900 text-lg font-semibold">
                {computedRetentionValue?.toLocaleString("es-MX", {
                  style: "currency",
                  currency: "MXN",
                }) || "$0.00"}
              </div>
            </div>
          )}

          {alreadyWithdrawn > 0 && (
            <div className="bg-background p-4 mt-4">
              <div className="text-gray-700 text-sm">
                {t("alreadyWithdrawnLabel")}
              </div>
              <div className="text-gray-900 text-lg font-semibold">
                {alreadyWithdrawn.toLocaleString("es-MX", {
                  style: "currency",
                  currency: "MXN",
                })}
              </div>
            </div>
          )}

          {/* Cash Available block - Updated breakdown */}
          <div className="bg-background p-4 mt-4">
            <div className="text-gray-700 text-sm mb-2">
              {t("cashAvailableLabel")}
            </div>

            {/* Simplified Breakdown Grid */}
            <div className="space-y-2 text-sm">
              {/* Total Collected */}
              <div className="flex justify-between">
                <span>{t("totalCollectedLabel")}:</span>
                <span>
                  {collectedSum.toLocaleString("es-MX", {
                    style: "currency",
                    currency: "MXN",
                  })}
                </span>
              </div>

              {/* Previous Withdrawals Deduction */}
              <div className="flex justify-between text-red-600">
                <span>{t("withdrawalsDeductedLabel")}:</span>
                <span>
                  -
                  {alreadyWithdrawn.toLocaleString("es-MX", {
                    style: "currency",
                    currency: "MXN",
                  })}
                </span>
              </div>

              {/* Commission Calculation */}
              <div className="flex justify-between text-red-600">
                <span>
                  {t("commissionLabel")} ({retentionFeeRate * 100}%):
                </span>
                <span>
                  -
                  {computedRetentionValue.toLocaleString("es-MX", {
                    style: "currency",
                    currency: "MXN",
                  })}
                </span>
              </div>

              {/* Available Total */}
              <div className="flex justify-between pt-2 border-t border-gray-200 font-semibold">
                <span>{t("availableLabel")}:</span>
                <span className="text-primary">
                  {(
                    collectedSum -
                    alreadyWithdrawn -
                    computedRetentionValue
                  ).toLocaleString("es-MX", {
                    style: "currency",
                    currency: "MXN",
                  })}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-8">
            <h3 className="text-lg font-semibold">{t("howItWorks_title")}</h3>
            <p className="text-sm text-gray-600 mt-2">
              {t("howItWorks_description")}
            </p>
          </div>
          <Button
            label={t("continueButton")}
            onClick={handleContinue}
            type="primary"
            className="w-full mt-6"
          />
        </div>
      </AnimatedCard>
    </PageContainer>
  );
};

export default withAuth(withTranslations(LOCALE)(WithdrawPageStep1));
